import { Menu, MenuOpen } from '@mui/icons-material';
import { Box, Icon, Typography, styled, useTheme, type Theme } from '@mui/material';
import type { ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import { useSideNav } from '~/layouts/context';
import { formatTimeAgo } from '~/lib/datetime';
import { useResponsive } from '~/lib/responsive';
import { InlineEditTextField, type InlineEditTextFieldProps } from './forms/inline-edit-text-field';

const TITLE_FONT = {
  fontFamily: 'Lato, Arial, Helvetica, sans-serif',
  fontSize: '24px',
  fontWeight: 600,
  lineHeight: '28px',
};

// Titles ellipsize on wide screens, where they almost always fit, and wrap on narrow ones, where
// an ellipsis would hide most of the name with no non-hover way to read it.
const titleWrap = (theme: Theme) => ({
  whiteSpace: 'nowrap' as const,
  textOverflow: 'ellipsis',
  [theme.breakpoints.down('md')]: {
    whiteSpace: 'normal' as const,
    overflowWrap: 'anywhere' as const,
  },
});

const StyledInlineEditTitle = styled(InlineEditTextField)(({ theme }) => ({
  width: '70%',
  '& .MuiTypography-root': {
    ...TITLE_FONT,
    ...titleWrap(theme),
  },
  '& .MuiInputBase-root, & .MuiInputBase-input': {
    ...TITLE_FONT,
  },
}));

/** An editable page title. Its read view is the page's h1, like `titleText`. */
export const InlineEditTitle = (props: InlineEditTextFieldProps) => (
  <StyledInlineEditTitle labelComponent="h1" {...props} />
);

const ToolbarContainer = styled(Box)(({ theme }) => ({
  ...theme.mixins.toolbar,
  alignItems: 'center',
  background: theme.palette.common.white,
  borderBottom: '1px solid rgb(63, 63, 63, .15)',
  display: 'flex',
  position: 'sticky',
  top: '64px',
  width: '100%',
  zIndex: '999',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 1),
  },
}));

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignContent: 'center',
  alignItems: 'center',
  order: 1,
  flexGrow: 1,
  // May shrink below its content so a long title ellipsizes instead of overflowing main.
  minWidth: 0,
  '& > h1': {
    marginLeft: theme.spacing(1),
  },
}));

const TitleIcon = styled(Box)({
  fontSize: '35px',
  '& > span': {
    fontSize: '35px',
  },
  '& svg': {
    fontSize: '35px',
  },
});

const ChildrenContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.common.white,
  order: 2,
  marginRight: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    marginRight: theme.spacing(0),
  },
}));

const ActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  background: theme.palette.common.white,
  order: 3,
  textAlign: 'right',
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    bottom: '0',
    left: '0',
    width: '100%',
    justifyContent: 'center',
    borderTop: '1px solid #cccccc',
    padding: theme.spacing(1, 2),
    marginLeft: theme.spacing(0),
    zIndex: '1000',
    '& .MuiButton-root': {
      maxWidth: '100%',
      width: '100%',
    },
  },
}));
export interface ToolbarProps {
  actions?: ReactNode;
  /**
   * The document title. Defaults to `titleText` or `titleTextCustom` when either is a string;
   * pages with a `titleEditor` pass the name being edited.
   */
  pageTitle?: string;
  children?: ReactNode;
  createdAt?: string;
  titleIcon?: ReactNode;
  titleText?: ReactNode;
  titleEditor?: ReactNode;
  titleLabel?: string;
  titleTextCustom?: string;
  updatedAt?: string;
  breadcrumbsLabel?: ReactNode;
}

export const Toolbar = ({
  actions,
  pageTitle,
  children,
  createdAt,
  titleIcon,
  titleText,
  titleEditor,
  titleLabel,
  breadcrumbsLabel,
  titleTextCustom,
  updatedAt,
}: ToolbarProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isSmDown } = useResponsive();
  const { navOpen, toggleDrawer } = useSideNav();

  const documentTitle =
    pageTitle ?? (typeof titleText === 'string' ? titleText : undefined) ?? titleTextCustom;

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      {documentTitle && <Helmet title={documentTitle} />}
      <ToolbarContainer>
        <TitleContainer>
          {isSmDown && (
            <Box
              sx={{
                borderRight: '1px solid rgb(63, 63, 63, .15)',
                marginRight: theme.spacing(1),
              }}
            >
              <IconButton
                label={
                  navOpen
                    ? t('common.actions.close_navigation')
                    : t('common.actions.open_navigation')
                }
                sx={{
                  color: 'primary.main',
                  '& svg': {
                    fontSize: '30px',
                  },
                }}
                onClick={toggleDrawer}
              >
                {navOpen ? <Menu /> : <MenuOpen />}
              </IconButton>
            </Box>
          )}

          {titleIcon && (
            <TitleIcon>
              <Icon color="primary">{titleIcon}</Icon>
            </TitleIcon>
          )}

          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              marginLeft: '10px',
            }}
          >
            {titleEditor && <div>{titleEditor}</div>}

            {/* The toolbar title is the page's one h1; its h3 variant keeps the size. */}
            {titleText && (
              <Typography
                component="h1"
                variant="h3"
                sx={{ overflow: 'hidden', ...titleWrap(theme) }}
              >
                {titleText}
              </Typography>
            )}

            {titleTextCustom && (
              <Typography
                component="h1"
                variant="h3"
                sx={{ fontSize: '20px', overflow: 'hidden', ...titleWrap(theme) }}
              >
                {titleTextCustom}
              </Typography>
            )}

            {titleLabel && <p>{titleLabel}</p>}
            {breadcrumbsLabel && <div>{breadcrumbsLabel}</div>}

            {(updatedAt || createdAt) && (
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: '.8rem',
                }}
              >
                {updatedAt ? t('common.labels.updated') : t('common.labels.created')}{' '}
                {formatTimeAgo(updatedAt ?? createdAt!)}
              </Typography>
            )}
          </Box>
        </TitleContainer>

        <ChildrenContainer>{children}</ChildrenContainer>

        {actions !== undefined && <ActionsContainer>{actions}</ActionsContainer>}
      </ToolbarContainer>
    </Box>
  );
};
